<template lang="pug">
    nav(:class="{ 'is-expanded': expanded }" v-if="menu && menu.length")
        el-menu(
            :default-active="menuActivePath"
            class="el-menu-vertical"
            :router="true"
        )
            template(v-for="(item, index) in menu")
                el-menu-item(
                    :key="item.path + '-item'"
                    :class="menuItemIsActive(item) ? 'is-active' : 'is-not-active'"
                    :index="itemIndex(item)"
                    :route="itemRoute(item)"
                    @mousedown.native.stop.prevent="setAnimation"
                )
                    i(:class="`el-icon-fa-${item.icon.name} ${item.icon.group}`")
                    .label {{ item.label }}
                    span.menu-shared-chip(v-if="item.shared && isMultistore") {{ $t('shared') }}
                    //- Parent badge hides once the submenu is open — the child row
                    //- (e.g. Inbox) then carries its own count, so the parent's is redundant.
                    span.menu-badge(v-if="item.badge && !showChildren(item)") {{ item.badge }}
                    span.dropdown-arrow(
                        v-if="expanded && item.children && item.children.length"
                        :class="{ 'is-open': showChildren(item) }"
                        @mousedown.stop.prevent
                        @click.stop.prevent="toggleGroup(item)"
                    )
                        i.el-icon-arrow-down
                template(v-if="showChildren(item)")
                    el-menu-item(
                        v-for="child in item.children"
                        :key="child.path + '-child'"
                        class="menu-child-item"
                        :class="{ 'is-active': childIsActive(child) }"
                        :index="child.path"
                        :route="{ path: child.path }"
                        @mousedown.native.stop.prevent="setAnimation"
                    )
                        i(:class="`el-icon-fa-${child.icon.name} ${child.icon.group}`")
                        span.child-label {{ child.label }}
                        span.menu-shared-chip(v-if="child.shared && isMultistore") {{ $t('shared') }}
                        span.menu-badge(v-if="child.badge") {{ child.badge }}
</template>

<i18n>
{
    "de": {
        "shared": "geteilt",
        "Dashboard": "Dashboard",
        "Posts": "Beiträge",
        "Pages & Menus": "Seiten & Menüs",
        "Events": "Events",
        "Catalog": "Katalog",
        "Sales": "Umsatz",
        "Point Of Sale": "Kasse",
        "Marketing": "Marketing",
        "Messages": "Nachrichten",
        "Conversations": "Konversationen",
        "Communication": "Kommunikation",
        "Statistics": "Statistiken",
        "People": "Personen",
        "Files": "Dateien",
        "Form entries": "Formular Einträge",
        "Settings": "Einstellungen",
        "Table Reservations": "Reservierungen",
        "CityOffer Admin": "CityOffer Verwaltung",
        "Products": "Produkte",
        "Sub-Articles": "Unterartikel",
        "Categories": "Kategorien",
        "Attribute sets": "Attribut-Sets",
        "Attributes": "Attribute",
        "Orders": "Bestellungen",
        "Ordered Items": "Bestellte Artikel",
        "Shopping Carts": "Warenkörbe",
        "Wish Lists": "Wunschlisten",
        "Cash Movements": "Kassenbewegungen",
        "Packing List": "Packliste",
        "Shipping": "Versand",
        "Campaigns": "Kampagnen",
        "Newsletters": "Newsletter",
        "Subscription Topics": "Abonnement-Themen",
        "Coupons": "Gutscheine",
        "Accounts": "Accounts",
        "Websites": "Webauftritte",
        "Bookings": "Anmeldungen",
        "Registration forms": "Anmeldeformulare",
        "Basic": "Basis",
        "Business Card": "Visitenkarte",
        "Billing": "Abrechnung",
        "Provider costs": "Drittanbieterkosten",
        "Platform": "Plattform",
        "APIs and external code": "APIs und Fremdcode",
        "Design": "Design",
        "Sections": "Inhaltselemente",
        "Global Contents": "Globale Inhalte",
        "Document Templates": "Dokumentvorlagen",
        "Shop": "Shop",
        "Stocks": "Lager",
        "Points of sale": "Kassen",
        "Shipping methods": "Versandmethoden",
        "Payment methods": "Zahlungsarten",
        "Currencies": "Währungen",
        "Members": "Mitglieder",
        "Customers": "Kunden",
        "Customer Groups": "Kundengruppen",
        "Chatbot": "Chatbot",
        "Artificial intelligence": "Künstliche Intelligenz",
        "Chats": "Chats",
        "Knowledge Base": "Wissensdatenbank",
        "AI Tools": "KI-Tools",
        "Prompt Templates": "Prompt-Vorlagen",
        "Team": "Team"
    }
}
</i18n>

<script>
import _ from 'lodash';

import { useSetupStore, useAuthStore, useCommunicationStore, useTicketStore, useFormEntryStore } from '@/stores';
import { checkRestrictions } from '@/helpers';
import { apps, appMenuItems } from '@/apps/registry';

export default {
    name: 'Navigation',
    props: {
        expanded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            openGroups: {},
        };
    },
    computed: {
        conversationStore() {
            return useCommunicationStore();
        },
        // Realtime unread total (WS-driven) — drives the "Nachrichten" menu badge.
        messagesUnread() {
            return this.conversationStore.unreadCount;
        },
        ticketStore() {
            return useTicketStore();
        },
        formEntryStore() {
            return useFormEntryStore();
        },
        // Submissions nobody has opened yet.
        unreadFormEntries() {
            return this.formEntryStore.unreadCount;
        },
        // Tickets carrying something nobody has read. The badge shows THIS
        // rather than the open count on purpose: an open-ticket badge is
        // permanently non-zero on a real desk and becomes wallpaper, while an
        // unread badge clears by looking — which is what a badge is for. The
        // workload numbers stay on the list as status chips.
        unreadTickets() {
            return this.ticketStore.unreadCount;
        },
        // Our own support desk, read from /system.
        unreadPlatformTickets() {
            return this.ticketStore.platformUnreadCount;
        },
        activePath() {
            const currentPath = this.$route.path;
            const currentRouteName = this.$route.name;
            const currentTopLevelPath = this.getTopLevelPath(currentPath);
            const metaParent = _.get(this.$route, 'meta.parent');
            const context = _.get(this.$route, 'meta.context');
            const isSettingsContext = context === 'settings';

            for (const item of this.menu) {
                if (item.children && item.children.length) {
                    const exactChild = item.children.find((c) => c.path === currentPath);
                    if (exactChild) return exactChild.path;

                    if (
                        (metaParent && item.name === metaParent) ||
                        (isSettingsContext && item.name === 'settings-form')
                    ) {
                        const matchingChild = item.children.find((child) =>
                            this.isChildActive(child, currentPath, currentRouteName, currentTopLevelPath)
                        );
                        if (matchingChild) return matchingChild.path;
                        return item.children[0].path;
                    }
                }
            }

            let path = '';

            let item = this.menu.find((item) => !item.children && item.path === currentPath);
            if (item) {
                path = item.path;
            }

            if (path === '') {
                if (metaParent === 'system' && context === 'settings' && _.get(this.$route, 'params.id') == null) {
                    item = this.menu.find((item) => item.name === context);
                    if (item) path = item.path;
                } else {
                    let match = this.$route.matched.find((match) => !!_.get(match, 'meta.parent'));
                    if (match) {
                        const parent = match.meta.parent;
                        item = this.menu.find((item) => item.name === parent && !item.children);
                        if (item) path = item.path;
                    }
                }
            }

            if (path === '') {
                this.$route.matched.forEach((match) => {
                    item = this.menu.find((item) => !item.children && item.name === match.name);
                    if (item) path = item.path;
                });
            }

            return path;
        },
        menuActivePath() {
            if (!this.expanded && this.activeGroupItem) {
                return this.getCollapsedPath(this.activeGroupItem);
            }
            return this.activePath;
        },
        activeGroupItem() {
            const metaParent = _.get(this.$route, 'meta.parent');
            const context = _.get(this.$route, 'meta.context');

            if (context === 'settings') {
                const settingsItem = this.menu.find((item) => item.children && item.name === 'settings-form');
                if (settingsItem) return settingsItem;
            }

            const byMetaParent = this.menu.find((item) => item.children && item.name === metaParent);
            if (byMetaParent) return byMetaParent;

            // Fallback for grouped routes without meta.parent (e.g. /posts/:key and /post/:key/:id/*)
            return (
                this.menu.find((item) => item.children && item.children.some((child) => this.childIsActive(child))) ||
                null
            );
        },
        menu() {
            let items = [
                {
                    path: '/',
                    name: 'dashboard',
                    label: this.$t('Dashboard'),
                    icon: {
                        name: 'th-large',
                        group: 'fas',
                    },
                },
            ];

            const categories = _.get(this.activeCms, 'config.template.categories') || [];
            const postChildren = categories
                .filter((category) => !!category.key)
                .map((category) => ({
                    path: `/posts/${category.key}`,
                    name: `posts-${category.key}`,
                    label: category.name || category.key,
                    icon: { name: 'newspaper', group: 'far' },
                }));

            if (checkRestrictions(['cp:posts']) && postChildren.length) {
                items.push({
                    path: '/posts',
                    name: 'posts',
                    label: this.$t('Posts'),
                    icon: {
                        name: 'newspaper',
                        group: 'far',
                    },
                    children: postChildren,
                });
            }

            let menus = _.get(this.activeCms, 'config.template.menus') || [];
            if (menus.length && checkRestrictions(['cp:menus'])) {
                menus = _.orderBy(menus, 'position');
                const menuChildren = menus
                    .filter((menu) => !!menu.key && this.checkMenuConditions(menu))
                    .map((menu) => ({
                        path: `/menu/${menu.key}`,
                        name: `menu-${menu.key}`,
                        label: menu.name || menu.key,
                        icon: { name: 'bars', group: 'fas' },
                    }));

                items.push({
                    path: '/menu',
                    name: 'menu-form',
                    label: this.$t('Pages & Menus'),
                    icon: {
                        name: 'bars',
                        group: 'fas',
                    },
                    children: menuChildren.length ? menuChildren : undefined,
                });
            }

            if (this.enabledExtensions.includes('event-manager') && checkRestrictions(['ex:evnt'])) {
                const children = [];
                if (checkRestrictions(['ex:evnt', 'ex:evnt:events'])) {
                    children.push({
                        path: '/event-manager/events',
                        name: 'events',
                        label: this.$t('Events'),
                        icon: { name: 'calendar-alt', group: 'fas' },
                    });
                }
                if (checkRestrictions(['ex:evnt', 'ex:evnt:categories'])) {
                    children.push({
                        path: '/event-manager/categories',
                        name: 'event-category-list',
                        label: this.$t('Categories'),
                        icon: { name: 'folder', group: 'far' },
                    });
                }
                if (checkRestrictions(['ex:evnt', 'ex:evnt:bookings'])) {
                    children.push({
                        path: '/event-manager/bookings',
                        name: 'event-booking-list',
                        label: this.$t('Bookings'),
                        icon: { name: 'calendar-check', group: 'far' },
                    });
                }
                // registration forms are a storefront feature (citystore-ssr)
                if (
                    checkRestrictions(['ex:evnt', 'ex:evnt:events'])
                    && this.enabledFeatures.includes('event-manager:registration-templates')
                ) {
                    children.push({
                        path: '/event-manager/registration-templates',
                        name: 'event-registration-template-list',
                        label: this.$t('Registration forms'),
                        icon: { name: 'clipboard-list', group: 'fas' },
                    });
                }
                if (children.length) {
                    items.push({
                        path: '/event-manager',
                        name: 'event-manager',
                        label: this.$t('Events'),
                        icon: { name: 'calendar-alt', group: 'fas' },
                        children,
                    });
                }
            }

            if (
                this.enabledExtensions.includes('shop') &&
                checkRestrictions(['ex:shop']) &&
                (checkRestrictions(['ex:shop:products']) ||
                    checkRestrictions(['ex:shop:categories']) ||
                    checkRestrictions(['ex:shop:prop-sets']) ||
                    checkRestrictions(['ex:shop:properties']))
            ) {
                const children = [];
                if (checkRestrictions(['ex:shop', 'ex:shop:products'])) {
                    children.push({
                        path: '/catalog/products',
                        name: 'product-list',
                        label: this.$t('Products'),
                        icon: { name: 'tag', group: 'fas' },
                    });
                    children.push({
                        path: '/catalog/articles',
                        name: 'article-list',
                        label: this.$t('Sub-Articles'),
                        icon: { name: 'tags', group: 'fas' },
                    });
                }
                if (checkRestrictions(['ex:shop', 'ex:shop:categories'])) {
                    children.push({
                        path: '/catalog/category',
                        name: 'category-form',
                        label: this.$t('Categories'),
                        icon: { name: 'folder', group: 'far' },
                    });
                }
                if (checkRestrictions(['ex:shop', 'ex:shop:prop-sets'])) {
                    children.push({
                        path: '/catalog/property-sets',
                        name: 'prop-set-list',
                        label: this.$t('Attribute sets'),
                        icon: { name: 'cubes', group: 'fas' },
                    });
                }
                if (checkRestrictions(['ex:shop', 'ex:shop:properties'])) {
                    children.push({
                        path: '/catalog/properties',
                        name: 'property-list',
                        label: this.$t('Attributes'),
                        icon: { name: 'cube', group: 'fas' },
                    });
                }
                items.push({
                    path: '/catalog',
                    name: 'catalog',
                    label: this.$t('Catalog'),
                    icon: { name: 'tags', group: 'fas' },
                    // Multistore: the whole catalog lives on the shared shop.
                    shared: true,
                    children,
                });
            }

            {
                const hasShop = this.enabledExtensions.includes('shop');
                const children = [];
                if (hasShop && checkRestrictions(['ex:shop', 'ex:shop:purchases'])) {
                    children.push({
                        path: '/sales/orders',
                        name: 'purchases',
                        label: this.$t('Orders'),
                        icon: { name: 'euro-sign', group: 'fas' },
                    });
                    children.push({
                        path: '/sales/items',
                        name: 'purchased-items',
                        label: this.$t('Ordered Items'),
                        icon: { name: 'tags', group: 'fas' },
                    });
                }
                if (
                    hasShop &&
                    this.enabledExtensions.includes('shop:point-of-sale') &&
                    checkRestrictions(['ex:pos', 'ex:pos:points-of-sale'])
                ) {
                    children.push({
                        path: '/sales/cash-flow-logs',
                        name: 'cash-flow-logs',
                        label: this.$t('Cash Movements'),
                        icon: { name: 'cash-register', group: 'fas' },
                    });
                }
                if (hasShop && checkRestrictions(['ex:shop', 'ex:shop:shopping-carts'])) {
                    children.push({
                        path: '/sales/carts',
                        name: 'cart-list',
                        label: this.$t('Shopping Carts'),
                        icon: { name: 'shopping-cart', group: 'fas' },
                    });
                }
                if (hasShop && checkRestrictions(['ex:shop', 'ex:shop:wish-lists'])) {
                    children.push({
                        path: '/sales/wish-lists',
                        name: 'wish-list-list',
                        label: this.$t('Wish Lists'),
                        icon: { name: 'heart', group: 'fas' },
                    });
                }
                if (hasShop && this.hasStockPermission) {
                    children.push({
                        path: '/sales/reserve',
                        name: 'sales-reserve',
                        label: this.$t('Packing List'),
                        icon: { name: 'box', group: 'fas' },
                    });
                    children.push({
                        path: '/sales/shipping',
                        name: 'sales-shipping',
                        label: this.$t('Shipping'),
                        icon: { name: 'truck', group: 'fas' },
                    });
                }
                if (children.length) {
                    items.push({
                        path: '/sales',
                        name: 'sales',
                        label: this.$t('Sales'),
                        icon: { name: 'euro-sign', group: 'fas' },
                        children,
                    });
                }
            }

            if (
                this.enabledExtensions.includes('shop') &&
                this.enabledExtensions.includes('shop:point-of-sale') &&
                checkRestrictions(['ex:pos'])
            ) {
                items.push({
                    path: '/point-of-sale',
                    name: 'point-of-sale',
                    label: this.$t('Point Of Sale'),
                    icon: {
                        name: 'cash-register',
                        group: 'fas',
                    },
                });
            }

            if (
                this.enabledExtensions.includes('table-reservations') &&
                checkRestrictions(['ex:trsv', 'ex:trsv:reservations'])
            ) {
                items.push({
                    path: '/table-reservations',
                    name: 'table-reservations',
                    label: this.$t('Table Reservations'),
                    icon: {
                        name: 'concierge-bell',
                        group: 'fas',
                    },
                });
            }

            if (
                (this.enabledExtensions.includes('marketing') &&
                    checkRestrictions(['ex:marketing', 'ex:marketing:campaigns'])) ||
                (this.enabledExtensions.includes('marketing') &&
                    checkRestrictions(['ex:marketing', 'ex:marketing:meta-pixel'])) ||
                (this.enabledExtensions.includes('marketing') &&
                    checkRestrictions(['ex:marketing', 'ex:marketing:google-tag-manager'])) ||
                (this.enabledExtensions.includes('shop') && checkRestrictions(['ex:shop', 'ex:shop:coupons'])) ||
                (this.enabledExtensions.includes('newsletter') && checkRestrictions(['ex:newsletter'])) ||
                checkRestrictions(['statistics'])
            ) {
                const children = [];
                if (checkRestrictions(['statistics'])) {
                    children.push({
                        path: '/marketing/statistics',
                        name: 'statistics',
                        label: this.$t('Statistics'),
                        icon: { name: 'chart-bar', group: 'fas' },
                    });
                }
                if (
                    this.enabledExtensions.includes('marketing') &&
                    checkRestrictions(['ex:marketing', 'ex:marketing:campaigns'])
                ) {
                    children.push({
                        path: '/marketing/campaigns',
                        name: 'campaigns',
                        label: this.$t('Campaigns'),
                        icon: { name: 'bullhorn', group: 'fas' },
                    });
                }
                if (
                    this.enabledExtensions.includes('marketing') &&
                    checkRestrictions(['ex:marketing', 'ex:marketing:meta-pixel'])
                ) {
                    children.push({
                        path: '/marketing/meta-pixel',
                        name: 'meta-pixel',
                        label: this.$t('Meta Pixel'),
                        icon: { name: 'crosshairs', group: 'fas' },
                    });
                }
                if (
                    this.enabledExtensions.includes('marketing') &&
                    checkRestrictions(['ex:marketing', 'ex:marketing:google-tag-manager'])
                ) {
                    children.push({
                        path: '/marketing/google-tag-manager',
                        name: 'google-tag-manager',
                        label: this.$t('Google Tag Manager'),
                        icon: { name: 'tags', group: 'fas' },
                    });
                }
                if (this.enabledExtensions.includes('newsletter') && checkRestrictions(['ex:newsletter'])) {
                    children.push({
                        path: '/marketing/newsletters',
                        name: 'newsletters',
                        label: this.$t('Newsletters'),
                        icon: { name: 'envelope', group: 'far' },
                    });
                    if (checkRestrictions(['ex:newsletter', 'ex:newsletter:topics'])) {
                        children.push({
                            path: '/marketing/newsletter-subscription-topics',
                            name: 'newsletter-subscription-topics',
                            label: this.$t('Subscription Topics'),
                            icon: { name: 'list', group: 'fas' },
                        });
                    }
                }
                if (this.enabledExtensions.includes('shop') && checkRestrictions(['ex:shop', 'ex:shop:coupons'])) {
                    children.push({
                        path: '/marketing/coupons',
                        name: 'coupons',
                        label: this.$t('Coupons'),
                        icon: { name: 'money-check', group: 'fas' },
                        shared: true,
                    });
                }
                items.push({
                    path: '/marketing',
                    name: 'marketing',
                    label: this.$t('Marketing'),
                    icon: { name: 'chart-line', group: 'fas' },
                    children,
                });
            }

            const showMembers =
                (this.enabledExtensions.includes('restricted-area') &&
                    checkRestrictions(['ex:rstrct', 'ex:rstrct:members'])) ||
                (this.enabledExtensions.includes('shop') && checkRestrictions(['ex:shop', 'ex:rstrct:members']));
            const showCustomerGroups = this.enabledExtensions.includes('shop') && checkRestrictions(['ex:shop']);
            const peopleLabel = this.enabledExtensions.includes('shop') ? this.$t('Customers') : this.$t('Members');

            if (showMembers || showCustomerGroups) {
                const peopleChildren = [];

                if (showMembers) {
                    peopleChildren.push({
                        path: '/members',
                        name: 'member-list',
                        label: peopleLabel,
                        icon: { name: 'user-friends', group: 'fas' },
                    });
                }

                if (showCustomerGroups) {
                    peopleChildren.push({
                        path: '/people/customer-groups',
                        name: 'customer-groups',
                        label: this.$t('Customer Groups'),
                        icon: { name: 'users', group: 'fas' },
                        shared: true,
                    });
                }

                if (peopleChildren.length === 1) {
                    const onlyChild = peopleChildren[0];
                    items.push({
                        path: onlyChild.path,
                        name: onlyChild.name,
                        label: peopleLabel,
                        icon: { name: 'user-friends', group: 'fas' },
                    });
                } else {
                    items.push({
                        path: '/people',
                        name: 'people',
                        label: peopleLabel,
                        icon: { name: 'user-friends', group: 'fas' },
                        children: peopleChildren,
                    });
                }
            }

            const hasCommunication =
                this.enabledExtensions.includes('communication') && checkRestrictions(['ex:comm', 'ex:comm:messages']);
            const hasFormEntries =
                this.enabledExtensions.includes('forms') && checkRestrictions(['ex:frms', 'ex:frms:entries']);
            const hasTickets =
                this.enabledExtensions.includes('tickets') && checkRestrictions(['ex:tckt', 'ex:tckt:tickets']);

            // The "Nachrichten" hub groups the message-like surfaces
            // (conversations, form entries, tickets). It only becomes a group
            // when there is more than one of them: as separate TOP-LEVEL items
            // they mirror the same tab bar, so switching a tab moved the active
            // main-menu item between two unrelated entries.
            const unread = this.messagesUnread > 0 ? this.messagesUnread : undefined;
            const hubChildren = [];

            if (hasCommunication) {
                hubChildren.push({
                    path: '/conversations',
                    name: 'messages-inbox',
                    label: this.$t('Conversations'),
                    icon: { name: 'comments', group: 'far' },
                    badge: unread,
                });
            }

            if (hasFormEntries) {
                hubChildren.push({
                    path: '/form-entries',
                    name: 'form-entries',
                    label: this.$t('Form entries'),
                    icon: { name: 'edit', group: 'fas' },
                    badge: this.unreadFormEntries > 0 ? this.unreadFormEntries : undefined,
                });
            }

            if (hasTickets) {
                hubChildren.push({
                    path: '/tickets',
                    name: 'tickets',
                    label: this.$t('Tickets'),
                    icon: { name: 'ticket', group: 'fas' },
                    badge: this.unreadTickets > 0 ? this.unreadTickets : undefined,
                });
            }

            if (hubChildren.length > 1) {
                // The collapsed parent stands in for its children, so it carries
                // everything they would show — not just the unread messages.
                const hubTotal = hubChildren.reduce((total, child) => total + (child.badge || 0), 0);

                items.push({
                    path: hubChildren[0].path,
                    name: 'messages',
                    label: this.$t('Messages'),
                    icon: { name: 'comments', group: 'far' },
                    badge: hubTotal > 0 ? hubTotal : undefined,
                    children: hubChildren,
                });
            } else if (hubChildren.length === 1) {
                // A hub of one is just the page itself.
                items.push(hubChildren[0]);
            }

            // AI Tools groups the chatbot's chats, the knowledge base (shared by
            // the chatbot and KI-Inhalte) and the generative-media prompt
            // templates — each child gated by its own extension; the parent shows
            // when at least one is enabled. ai-image-edit is SYSTEM-WIDE (never
            // template-listed) → checked against the CMS's active extensions, not
            // the template-filtered enabledExtensions.
            if (this.hasKnowledgeBase || this.activeExtensions.includes('ai-image-edit')) {
                const aiToolsChildren = [];

                if (this.enabledExtensions.includes('chatbot')) {
                    aiToolsChildren.push({
                        path: '/chatbot/chats',
                        name: 'chatbot-conversations',
                        label: this.$t('Chats'),
                        icon: { name: 'comments', group: 'fas' },
                    });
                }

                if (this.hasKnowledgeBase) {
                    aiToolsChildren.push({
                        path: '/ai-tools/knowledge-base',
                        name: 'knowledge-base',
                        label: this.$t('Knowledge Base'),
                        icon: { name: 'book', group: 'fas' },
                    });
                }

                if (this.activeExtensions.includes('ai-image-edit')) {
                    aiToolsChildren.push({
                        path: '/ai-tools/prompt-templates',
                        name: 'ai-prompt-templates',
                        label: this.$t('Prompt Templates'),
                        icon: { name: 'wand-magic-sparkles', group: 'fas' },
                    });
                }

                items.push({
                    path: '/ai-tools',
                    name: 'ai-tools',
                    label: this.$t('AI Tools'),
                    icon: { name: 'robot', group: 'fas' },
                    children: aiToolsChildren,
                });
            }

            if (checkRestrictions(['files'])) {
                items.push({
                    path: '/files',
                    name: 'files',
                    label: this.$t('Files'),
                    icon: {
                        name: 'paste',
                        group: 'fas',
                    },
                    // One merchant, one media library: the list spans every store of
                    // the shop, so a satellite sees the parent's files here. Chip only
                    // - unlike the shop-owned screens, each store still uploads and
                    // manages its own media, so no `sharedWithShop` route meta.
                    shared: true,
                });
            }

            // Tenant apps: one top-level entry per app whose extension is
            // attached to this CMS (see src/apps/registry.js).
            items.push(
                ...appMenuItems(apps, {
                    activeExtensions: this.activeExtensions,
                    checkRestrictions,
                    translate: (label) => this.$t(label),
                })
            );

            if (checkRestrictions(['settings'])) {
                const settingsChildren = this.getSettingsMenuChildren();
                items.push({
                    path: '/settings',
                    name: 'settings-form',
                    label: this.$t('Settings'),
                    icon: {
                        name: 'cog',
                        group: 'fas',
                    },
                    children: settingsChildren,
                });
            }

            if (checkRestrictions(['superuser'])) {
                // Each entry carries the platform right its screen enforces, so
                // the menu never offers a door that answers 403. Same rule and
                // same keys as the /system tab bar - a new screen needs BOTH,
                // and forgetting the sidebar half is the classic miss.
                const held = useAuthStore().platformRightKeys;
                const systemChildren = [
                    {
                        path: '/system/accounts',
                        name: 'system-accounts',
                        label: this.$t('Accounts'),
                        icon: { name: 'user-tie', group: 'fas' },
                        right: 'platform:accounts',
                    },
                    {
                        path: '/system/abonements',
                        name: 'system-abonements',
                        label: this.$t('Websites'),
                        icon: { name: 'cubes', group: 'fas' },
                        right: 'platform:accounts',
                    },
                    {
                        path: '/system/billing',
                        name: 'system-billing',
                        label: this.$t('Billing'),
                        icon: { name: 'file-invoice-dollar', group: 'fas' },
                        right: 'platform:billing',
                    },
                    {
                        path: '/system/tickets',
                        name: 'system-tickets',
                        label: this.$t('Tickets'),
                        icon: { name: 'ticket', group: 'fas' },
                        // OUR desk's unread — the same number the /system
                        // tab bar shows. The shop menu's Tickets entry
                        // carries the tenant desk's, which is a different
                        // number about different tickets.
                        badge: this.unreadPlatformTickets > 0 ? this.unreadPlatformTickets : undefined,
                    },
                    {
                        path: '/system/team',
                        name: 'system-team',
                        label: this.$t('Team'),
                        icon: { name: 'users', group: 'fas' },
                        right: 'platform:support-time',
                    },
                    {
                        path: '/system/provider-costs',
                        name: 'system-provider-costs',
                        label: this.$t('Provider costs'),
                        icon: { name: 'hand-holding-usd', group: 'fas' },
                        right: 'platform:provider-costs',
                    },
                    {
                        path: '/system/settings',
                        name: 'system-settings',
                        label: this.$t('Platform'),
                        icon: { name: 'sliders', group: 'fas' },
                        right: 'platform:system',
                    },
                ].filter((child) => !child.right || held.indexOf(child.right) !== -1);

                // The collapsed parent stands in for its children, so it
                // carries what they would show - the same rule the message hub
                // above follows. Without it the unread tickets vanish the
                // moment somebody folds the group away.
                const systemTotal = systemChildren.reduce((total, child) => total + (child.badge || 0), 0);

                items.push({
                    path: '/system',
                    name: 'system',
                    label: this.$t('CityOffer Admin'),
                    icon: { name: 'asterisk', group: 'fas' },
                    badge: systemTotal > 0 ? systemTotal : undefined,
                    children: systemChildren,
                });
            }

            // Without an active CMS (a super-user who hasn't entered a tenant yet),
            // no CMS-scoped navigation applies — show only the non-CMS super-user
            // administration ("CityOffer Admin"), never Dashboard/Marketing/Files/etc.
            if (!_.get(this.activeCms, 'id')) {
                return items.filter((item) => item.name === 'system');
            }

            return items;
        },
        activeCms() {
            return useSetupStore().activeCms;
        },
        // Multistore: the shared-data chip only makes sense once the shop
        // actually carries more than one store.
        isMultistore() {
            return (_.get(this.activeCms, 'shop.cmses') || []).length > 1;
        },
        enabledExtensions() {
            return useSetupStore().enabledExtensions;
        },
        enabledFeatures() {
            return useSetupStore().enabledFeatures;
        },
        activeExtensions() {
            return useSetupStore().activeExtensions;
        },
        hasKnowledgeBase() {
            return useSetupStore().hasKnowledgeBase;
        },

        settingValues() {
            return useSetupStore().settingValues;
        },
        rights() {
            return useAuthStore().rights;
        },
        isOwner() {
            return useAuthStore().isOwner;
        },
        hasStockPermission() {
            if (this.isOwner) return true;

            if (checkRestrictions(['ex:shop', 'ex:shop:stocks'])) {
                const right = this.rights.find((right) => right.key === 'ex:shop:stocks');
                if (
                    right &&
                    (right.permission.any === true ||
                        (right.permission.any === false && right.permission.stocks.length))
                ) {
                    return true;
                }
            }

            return false;
        },
    },
    mounted() {
        this.syncRealtime();
        document.addEventListener('echo:connected', this.syncRealtime);
    },
    beforeDestroy() {
        document.removeEventListener('echo:connected', this.syncRealtime);
    },
    watch: {
        activeCms() {
            this.syncMessagesUnreadSubscription();
            this.syncOpenTicketCount();
            this.syncUnreadFormEntryCount();
        },
        $route() {
            // Reading/marking a thread changes the count — refresh on navigation.
            this.syncMessagesUnreadSubscription();
            this.syncOpenTicketCount();
            this.syncPlatformTicketCount();
            this.syncUnreadFormEntryCount();
        },
    },
    methods: {
        // Every badge here is filled once and then moved by the socket. A
        // broadcast sent while the socket was down is not replayed, so when it
        // reconnects (echo.js) each store asks again; the desk tick reaches the
        // header's clock, which otherwise kept counting a session stopped from
        // another browser while this one slept.
        syncRealtime() {
            this.syncMessagesUnreadSubscription();
            this.syncOpenTicketCount();
            this.syncPlatformTicketCount();
            this.syncUnreadFormEntryCount();
            this.ticketStore.deskChanged();
        },
        syncMessagesUnreadSubscription() {
            const cms = this.activeCms;
            if (
                !cms ||
                !cms.id ||
                !this.enabledExtensions.includes('communication') ||
                !checkRestrictions(['ex:comm', 'ex:comm:messages'])
            ) {
                this.conversationStore.unsubscribeRealtime();
                this.conversationStore.resetUnread();
                return;
            }
            // Subscribe once per CMS; the store keeps the badge live over WebSocket.
            this.conversationStore.subscribeRealtime();
        },
        // Tickets have no realtime channel, so the badge is refreshed whenever
        // the shop changes and after the ticket surfaces write.
        syncUnreadFormEntryCount() {
            const cms = this.activeCms;

            if (
                !cms ||
                !cms.id ||
                !this.enabledExtensions.includes('forms') ||
                !checkRestrictions(['ex:frms', 'ex:frms:entries'])
            ) {
                this.formEntryStore.unsubscribeRealtime();
                this.formEntryStore.resetUnreadCount();
                return;
            }

            this.formEntryStore.subscribeRealtime(cms.id);
        },
        // The platform desk is not a CMS context, so it has its own channel and
        // its own count — and the sidebar must show it from ANY page, not just
        // while a /system screen is mounted. Inside a customer's account the
        // token's user is the customer, but the desk is still ours (the same
        // rule that shows the platform pill, Auth.vue `canTrackPlatformTime`):
        // judged by super user only, no tab in a mimic listened to it, and the
        // pill there heard nothing.
        syncPlatformTicketCount() {
            if (!checkRestrictions(['superuser']) && !useAuthStore().isImpersonatedBySuperUser) {
                this.ticketStore.unsubscribePlatformRealtime();
                this.ticketStore.resetPlatformUnreadCount();
                return;
            }

            this.ticketStore.subscribePlatformRealtime();
        },

        syncOpenTicketCount() {
            const cms = this.activeCms;

            if (
                !cms ||
                !cms.id ||
                !this.enabledExtensions.includes('tickets') ||
                !checkRestrictions(['ex:tckt', 'ex:tckt:tickets'])
            ) {
                this.ticketStore.unsubscribeRealtime();
                this.ticketStore.resetOpenCount();
                return;
            }

            // Subscribe once per CMS; the store keeps the badge live over WebSocket.
            this.ticketStore.subscribeRealtime(cms.id, 'tenant');
        },
        checkMenuConditions(menu) {
            let valid = true;

            // eslint-disable-next-line no-unused-vars
            const settings = this.settingValues;
            const enabledExtensions = this.enabledExtensions;

            try {
                const conditions = menu.conditions;
                if (conditions && conditions.length) {
                    conditions.forEach((conditionItem) => {
                        if (valid === false) return;

                        const { type, condition } = conditionItem;
                        switch (type) {
                            case 'eval':
                                valid = eval(condition);
                                break;

                            default:
                                valid = false;
                        }
                    });
                }
            } catch (error) {
                console.error(error);
                valid = false;
            }

            return valid;
        },
        getSettingsMenuChildren() {
            const children = [];
            const templateSettings = _.get(this.activeCms, 'config.template.settings') || [];
            const templateRequirements = _.get(this.activeCms, 'config.template.requirements') || [];
            const templateGlobalContents = _.get(this.activeCms, 'config.template.globalContents');
            const activeExtensions = (_.get(this.activeCms, 'extensions') || []).map((extension) => extension.key);
            const hasShop = activeExtensions.includes('shop');
            const hasChatbot = activeExtensions.includes('chatbot');

            const hasRequirement = (key, context) => {
                return !!templateRequirements.find(
                    (requirement) => requirement.key === key && requirement.context === context
                );
            };

            children.push({
                path: '/settings/basic',
                name: 'settings-basic',
                label: this.$t('Basic'),
                icon: { name: 'gear', group: 'fas' },
            });

            if (_.get(this.activeCms, 'is_business_card')) {
                children.push({
                    path: '/settings/business-card',
                    name: 'settings-business-card',
                    label: this.$t('Business Card'),
                    icon: { name: 'address-card', group: 'fas' },
                });
            }

            children.push({
                path: '/settings/billing',
                name: 'settings-billing',
                label: this.$t('Billing'),
                icon: { name: 'file-invoice-dollar', group: 'fas' },
            });

            // SMTP / newsletter config moved to the Communication tab — no longer
            // a reason to show the API tab.
            const showApiTab =
                hasRequirement('mapbox', 'cms') ||
                hasRequirement('google-recaptcha-v3', 'cms') ||
                (hasShop && hasRequirement('paypal-checkout', 'shop')) ||
                (hasShop && hasRequirement('stripe-checkout', 'shop'));

            if (showApiTab) {
                children.push({
                    path: '/settings/api',
                    name: 'settings-api',
                    label: this.$t('APIs and external code'),
                    icon: { name: 'key', group: 'fas' },
                });
            }

            if (templateSettings.find((setting) => setting.key === 'design')) {
                children.push({
                    path: '/settings/design',
                    name: 'settings-design',
                    label: this.$t('Design'),
                    icon: { name: 'brush', group: 'fas' },
                });
            }

            if (templateSettings.find((setting) => setting.key === 'sections')) {
                children.push({
                    path: '/settings/sections',
                    name: 'settings-sections',
                    label: this.$t('Sections'),
                    icon: { name: 'puzzle-piece', group: 'fas' },
                });
            }

            if (templateGlobalContents) {
                children.push({
                    path: '/settings/global-contents',
                    name: 'settings-global-contents',
                    label: this.$t('Global Contents'),
                    icon: { name: 'globe', group: 'fas' },
                });
            }

            children.push({
                path: '/settings/document-templates',
                name: 'settings-document-templates',
                label: this.$t('Document Templates'),
                icon: { name: 'file', group: 'fas' },
            });

            // Communication — hosts the transactional + newsletter SMTP config (needed
            // by shop/forms/newsletter CMSes) plus, when the communication feature is
            // on, the WhatsApp/messenger channels and signatures. Show whenever any of
            // those apply.
            const hasCommunicationFeature =
                activeExtensions.includes('communication') && checkRestrictions(['ex:comm', 'ex:comm:accounts']);
            const hasNewsletter = activeExtensions.includes('newsletter');
            const needsSmtp = hasRequirement('smtp-credentials', 'cms');
            if (hasCommunicationFeature || hasNewsletter || needsSmtp) {
                children.push({
                    path: '/settings/communication',
                    name: 'settings-communication',
                    label: this.$t('Communication'),
                    icon: { name: 'comments', group: 'far' },
                });
            }

            if (hasShop && checkRestrictions('ex:shop')) {
                children.push({
                    path: '/settings/shop',
                    name: 'settings-shop',
                    label: this.$t('Shop'),
                    icon: { name: 'shopping-cart', group: 'fas' },
                    // No chip: most of this screen is the store's own - its identity
                    // override, its numbering, its settings. The few shop-wide sections
                    // are marked where they are.
                });
            }

            if (hasShop && checkRestrictions(['ex:shop', 'ex:shop:stocks'])) {
                children.push({
                    path: '/settings/stocks',
                    name: 'settings-stocks',
                    label: this.$t('Stocks'),
                    icon: { name: 'house', group: 'fas' },
                    shared: true,
                });
            }

            // A terminal is a separately sold add-on (depends_on), not something a
            // shop carries - so this needs the POS extension itself, exactly like the
            // POS screens at :385 and :434. Without it every shop store showed a
            // Kassen settings page, satellites included.
            if (
                hasShop &&
                this.enabledExtensions.includes('shop:point-of-sale') &&
                checkRestrictions(['ex:shop', 'ex:pos', 'ex:pos:points-of-sale'])
            ) {
                children.push({
                    path: '/settings/pos',
                    name: 'settings-pos',
                    label: this.$t('Points of sale'),
                    icon: { name: 'cash-register', group: 'fas' },
                });
            }

            if (hasShop && checkRestrictions('ex:shop')) {
                children.push({
                    path: '/settings/shipping-methods',
                    name: 'shipping-methods',
                    label: this.$t('Shipping methods'),
                    icon: { name: 'truck', group: 'fas' },
                });
                children.push({
                    path: '/settings/payment-methods',
                    name: 'payment-methods',
                    label: this.$t('Payment methods'),
                    icon: { name: 'credit-card', group: 'fas' },
                });
                children.push({
                    path: '/settings/currencies',
                    name: 'currencies',
                    label: this.$t('Currencies'),
                    icon: { name: 'coins', group: 'fas' },
                    shared: true,
                });
            }

            // The knowledge base is shared by the chatbot and KI-Inhalte; the
            // chatbot's own tab holds only its persona.
            if (this.hasKnowledgeBase) {
                children.push({
                    path: '/settings/knowledge-base',
                    name: 'settings-knowledge-base',
                    label: this.$t('Knowledge Base'),
                    icon: { name: 'book', group: 'fas' },
                });
            }

            if (hasChatbot) {
                children.push({
                    path: '/settings/chatbot',
                    name: 'settings-chatbot',
                    label: this.$t('Chatbot'),
                    icon: { name: 'robot', group: 'fas' },
                });
            }

            // Art. 50 KI-VO labelling and the generative-media defaults. The route
            // (settings-ai) existed without a way to reach it from the menu.
            children.push({
                path: '/settings/ai',
                name: 'settings-ai',
                label: this.$t('Artificial intelligence'),
                icon: { name: 'wand-magic-sparkles', group: 'fas' },
            });

            return children;
        },
        getCollapsedPath(item) {
            if (item.children && item.children.length) {
                return item.children[0].path;
            }

            return item.path;
        },
        itemIndex(item) {
            return this.getCollapsedPath(item);
        },
        itemRoute(item) {
            if (item.children && item.children.length) {
                return { path: this.getCollapsedPath(item) };
            }

            return item;
        },
        menuItemIsActive(item) {
            if (item.children && item.children.length) {
                return item.children.some((child) => this.childIsActive(child));
            }

            return this.menuActivePath === this.itemIndex(item);
        },
        showChildren(item) {
            return this.expanded && item.children && item.children.length && this.isGroupOpen(item);
        },
        isGroupOpen(item) {
            const hasManualToggle = Object.prototype.hasOwnProperty.call(this.openGroups, item.path);
            if (hasManualToggle) {
                return this.openGroups[item.path];
            }

            return this.menuItemIsActive(item);
        },
        toggleGroup(item) {
            const nextState = !this.isGroupOpen(item);
            this.openGroups = {
                ...this.openGroups,
                [item.path]: nextState,
            };
        },
        getTopLevelPath(path) {
            const [first, second] = path.split('/').filter(Boolean);
            return second ? `/${first}/${second}` : path;
        },
        isChildActive(child, currentPath, currentRouteName, currentTopLevelPath) {
            if (child.path === currentPath) return true;
            if (child.name === currentRouteName) return true;
            if (child.path === currentTopLevelPath) return true;
            if (currentPath.startsWith(child.path + '/')) return true;

            // Marketing list routes are plural, while form/detail routes are singular.
            if (child.path === '/marketing/newsletters' && currentPath.startsWith('/marketing/newsletter/'))
                return true;
            if (child.path === '/marketing/campaigns' && currentPath.startsWith('/marketing/campaign/')) return true;
            if (
                child.path === '/marketing/newsletter-subscription-topics' &&
                currentPath.startsWith('/marketing/newsletter-subscription-topic/')
            )
                return true;

            // Chatbot list routes vs detail routes.
            if (child.path === '/chatbot/chats' && currentPath.startsWith('/chatbot/chat/')) return true;

            // Event manager list routes are plural, while form/detail routes are singular.
            if (child.path === '/event-manager/events' && currentPath.startsWith('/event-manager/event/')) return true;
            if (child.path === '/event-manager/categories' && currentPath.startsWith('/event-manager/category/'))
                return true;
            if (child.path === '/event-manager/bookings' && currentPath.startsWith('/event-manager/booking/'))
                return true;
            if (
                child.path === '/event-manager/registration-templates'
                && currentPath.startsWith('/event-manager/registration-template/')
            )
                return true;

            // Posts category children must also be active on post detail routes (/post/:key/:id/*)
            if (child.path.startsWith('/posts/')) {
                const routeKey = _.get(this.$route, 'params.key');
                const childKey = child.path.replace('/posts/', '');
                if (routeKey && childKey === routeKey) return true;
            }

            return false;
        },
        childIsActive(child) {
            const currentPath = this.$route.path;
            const currentRouteName = this.$route.name;
            const currentTopLevelPath = this.getTopLevelPath(currentPath);
            return this.isChildActive(child, currentPath, currentRouteName, currentTopLevelPath);
        },
        setAnimation() {
            useSetupStore().updateAnimation('slide-fade');
        },
    },
};
</script>

<style lang="sass" scoped>
// Multistore: marks nav entries whose data is shared across the stores of one
// shop - either living on the shop itself (catalog, coupons, stocks) or pooled
// across stores (the media library).
.menu-shared-chip
    display: inline-flex
    align-items: center
    margin-left: 6px
    padding: 0 6px
    height: 15px
    border-radius: 7px
    background: rgba(64, 158, 255, .12)
    color: #409EFF
    font-size: 9px
    letter-spacing: .3px
    text-transform: uppercase
    font-weight: 600
    white-space: nowrap
    vertical-align: middle

.menu-badge
    display: inline-flex
    align-items: center
    justify-content: center
    min-width: 16px
    height: 16px
    padding: 0 4px
    margin-left: 6px
    border-radius: 8px
    background: #f56c6c
    color: #fff
    font-size: 10px
    line-height: 16px
    font-weight: 600
    vertical-align: middle
    box-sizing: border-box

// Parent badge is a notification pill anchored to the ICON's top-right corner.
// The white ring separates it from the icon; no outer shadow (kept flat).
.el-menu-item:not(.menu-child-item) > .menu-badge
    position: absolute
    top: 6px
    // Mini sidebar: the item is centered (icon over label), so the icon sits at 50%.
    left: calc(50% + 6px)
    margin-left: 0
    border: 2px solid #fff
// Full sidebar: the icon sits at the left of the row, so anchor the badge over it
// (padding 14px + 18px icon ≈ its right edge) instead of drifting to the row centre.
nav.is-expanded .el-menu-item:not(.menu-child-item) > .menu-badge
    top: 7px
    left: 26px
.el-menu-vertical
    border: none
    text-align: center
    background: transparent
    &.el-menu
        position: relative
        left: -1px

.el-menu-item
    text-decoration: none
    display: block
    text-align: center
    height: auto
    line-height: 1.3
    padding: 10px !important
    border-bottom: 1px solid $color-body
    &:focus, &:hover
        background-color: rgba($color-selection, .6)
    &.is-active
        &.is-not-active
            color: #303133
            i
                color: #909399
        &:not(.is-not-active)
            color: $color-unselection
            i
                color: $ci-color-primary
            .label
                color: $ci-color-secondary
    &:last-child
        border-bottom: none
    > i
        margin: 0
        font-size: 1.2rem
    .label
        margin-top: 5px
        white-space: initial

.menu-child-item
    height: auto
    line-height: 1.2
    padding: 8px 10px !important
    border-bottom: 1px solid #e9edf2
    border-left: 0
    text-align: left
    display: flex
    align-items: center
    gap: 8px
    font-size: 0.8rem
    background: #fafbfd
    &:hover, &:focus
        background-color: #f4f7fb
    &.is-active
        color: $ci-color-primary
        i
            color: $ci-color-primary
    > i
        font-size: 0.85rem
        margin: 0
        flex-shrink: 0
        width: 14px
        text-align: center
    .child-label
        white-space: normal
        line-height: 1.2
    &:last-child
        border-bottom: 1px solid #e5eaf0

nav.is-expanded
    .el-menu-vertical
        text-align: left

    .el-menu-item
        display: flex
        align-items: center
        gap: 10px
        text-align: left
        padding: 12px 14px !important
        > i
            width: 18px
            font-size: 1rem
            text-align: center
            flex-shrink: 0
        .label
            flex: 1
            margin-top: 0
            line-height: 1.2
        .dropdown-arrow
            margin-left: auto
            display: inline-flex
            align-items: center
            color: #6d7480
            font-size: 0.8rem
            cursor: pointer
            i
                transition: transform .2s ease
            &.is-open i
                transform: rotate(180deg)

    .menu-child-item
        padding: 8px 12px 8px 28px !important
        .child-label
            flex: 1
</style>
